import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Search,
  Plus,
  Heart,
  Share2,
  ArrowRight,
  Flame,
  Clock,
  Star,
  X,
  Loader2,
  Wand2,
  Users,
  GitBranch,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SiteFooter from '../components/SiteFooter';
import AuthDialog from '../components/AuthDialog';
import BrandLogo from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Toaster } from '../components/ui/toaster';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_MAP = {
  'cyan-magenta': 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
  'silver-slate': 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
  'emerald-lime': 'linear-gradient(135deg, #10b981 0%, #a3e635 100%)',
  'amber-crimson': 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)',
  'sky-lavender': 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
  'violet-gold': 'linear-gradient(135deg, #8b5cf6 0%, #fbbf24 100%)',
};

function gradientFor(key) {
  return GRADIENT_MAP[key] || GRADIENT_MAP['cyan-magenta'];
}

const SORT_TABS = [
  { id: 'trending', label: 'Trending', icon: Flame },
  { id: 'top', label: 'Top', icon: Star },
  { id: 'new', label: 'New', icon: Clock },
];

export default function SoulLibraryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [souls, setSouls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('trending');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [remixSource, setRemixSource] = useState(null); // { slug, name, emoji, gradient, system_prompt, tagline }

  const loadSouls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('sort', sort);
      const r = await fetch(`${API}/souls?${params.toString()}`);
      if (r.ok) setSouls(await r.json());
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => {
    loadSouls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  useEffect(() => {
    const t = setTimeout(() => loadSouls(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Detect /souls?remix=<slug> — fetch source and open the modal pre-filled
  useEffect(() => {
    const remixSlug = searchParams.get('remix');
    if (!remixSlug) return;
    (async () => {
      try {
        const r = await fetch(`${API}/souls/${encodeURIComponent(remixSlug)}`);
        if (r.ok) {
          const src = await r.json();
          setRemixSource(src);
          setSubmitOpen(true);
        }
      } catch { /* noop */ }
    })();
  }, [searchParams]);

  const closeSubmit = () => {
    setSubmitOpen(false);
    setRemixSource(null);
    // Clean the URL so a refresh doesn't reopen the modal
    if (searchParams.get('remix')) {
      const next = new URLSearchParams(searchParams);
      next.delete('remix');
      setSearchParams(next, { replace: true });
    }
  };

  const featured = useMemo(() => souls.filter((s) => s.featured).slice(0, 3), [souls]);

  const activateSoul = async (soul) => {
    try {
      localStorage.setItem('atw_soul', soul.system_prompt);
      localStorage.setItem('atw_soul_meta', JSON.stringify({
        name: soul.name, emoji: soul.emoji, slug: soul.slug,
      }));
      fetch(`${API}/souls/${soul.slug}/use`, { method: 'POST' }).catch(() => {});
      toast({
        title: `${soul.emoji || '✨'} ${soul.name} activated`,
        description: 'The AI now speaks in this personality — head to the builder.',
      });
      setTimeout(() => navigate('/build'), 700);
    } catch (e) {
      toast({ title: 'Could not activate', description: e.message });
    }
  };

  const shareSoul = (soul) => {
    const url = `${BACKEND_URL}/api/share/soul/${soul.slug}`;
    try {
      navigator.clipboard.writeText(url);
      toast({ title: 'Share link copied', description: 'Paste it into Twitter/LinkedIn — the persona card previews automatically.' });
    } catch {
      toast({ title: 'Share URL', description: url });
    }
  };

  const likeSoul = async (soul) => {
    try {
      const r = await fetch(`${API}/souls/${soul.slug}/like`, { method: 'POST' });
      const d = await r.json();
      if (r.ok) {
        setSouls((cur) => cur.map((s) => s.slug === soul.slug ? { ...s, likes: d.likes } : s));
      } else {
        toast({ title: 'Already liked', description: d.detail || 'Come back tomorrow!' });
      }
    } catch { /* noop */ }
  };

  return (
    <div className="relative bg-[#030303] text-white min-h-screen overflow-hidden">
      <Navbar onGetStarted={() => setAuthOpen(true)} />
      {/* Ambient glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-30"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, #FF007F33 0%, transparent 70%)' }}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-14 px-6 lg:px-10 max-w-7xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-xl px-4 py-1.5 text-[11.5px] font-mono uppercase tracking-[0.3em] text-white/70"
        >
          <Sparkles size={12} className="text-[#00F0FF]" />
          <span>SOUL Library</span>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <span className="text-white/50">{souls.length} personalities</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-6 font-display font-black tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl"
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #e2e8f0 55%, #94a3b8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Give your AI a soul.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-4 max-w-2xl mx-auto text-[15.5px] leading-relaxed text-white/70"
        >
          A public gallery of AI personalities crafted by the community. Steve Jobs Mode. Yoda Mode.
          Your favourite coach. Tap one — it instantly reshapes how the builder talks to you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-8 flex items-center justify-center gap-3 flex-wrap"
        >
          <button
            onClick={() => setSubmitOpen(true)}
            data-testid="souls-submit-btn"
            className="inline-flex items-center gap-2 rounded-full text-black font-semibold text-[14px] px-5 py-2.5 hover:scale-[1.03] transition"
            style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
          >
            <Plus size={16} /> Publish your SOUL
          </button>
          <Link
            to="/build"
            data-testid="souls-open-builder"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-2.5 transition"
          >
            <Wand2 size={15} /> Open the Builder
          </Link>
          {typeof window !== 'undefined' && localStorage.getItem('atw_author_key') && (
            <Link
              to="/souls/mine"
              data-testid="souls-mine-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-2.5 transition"
            >
              <Users size={15} /> Your souls
            </Link>
          )}
        </motion.div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="relative px-6 lg:px-10 max-w-7xl mx-auto mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Flame size={14} className="text-amber-300" />
            <span className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">
              Featured personalities
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {featured.map((s) => (
              <SoulCard
                key={s.slug}
                soul={s}
                featured
                onActivate={activateSoul}
                onShare={shareSoul}
                onLike={likeSoul}
              />
            ))}
          </div>
        </section>
      )}

      {/* SEARCH + SORT */}
      <section className="relative px-6 lg:px-10 max-w-7xl mx-auto mt-14">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="souls-search-input"
              placeholder="Search personalities — 'coach', 'yoda', 'zen'…"
              className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white/[0.04] border border-white/10 text-[14.5px] text-white placeholder:text-white/35 focus:outline-none focus:border-white/25 transition"
            />
          </div>
          <div className="inline-flex rounded-2xl border border-white/10 bg-white/[0.04] p-1">
            {SORT_TABS.map((t) => {
              const Icon = t.icon;
              const active = sort === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSort(t.id)}
                  data-testid={`souls-sort-${t.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[12.5px] font-medium transition ${
                    active
                      ? 'bg-white/[0.1] text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <Icon size={13} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* GRID */}
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-white/50">
              <Loader2 size={22} className="animate-spin mr-2" /> Summoning souls…
            </div>
          ) : souls.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-white/50">
              No personalities match your search. Be the first to publish one!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="souls-grid">
              {souls.map((s) => (
                <SoulCard
                  key={s.slug}
                  soul={s}
                  onActivate={activateSoul}
                  onShare={shareSoul}
                  onLike={likeSoul}
                />
              ))}
            </div>
          )}
        </div>

        {/* Growth loop banner */}
        <div className="mt-14 rounded-3xl border border-white/10 overflow-hidden relative">
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{ background: 'radial-gradient(circle at 20% 20%, #00F0FF33, transparent 55%), radial-gradient(circle at 80% 80%, #FF007F33, transparent 55%)' }}
          />
          <div className="relative p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6">
            <BrandLogo size={64} showWordmark={false} />
            <div className="flex-1">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">A free growth loop</div>
              <div className="mt-1 text-2xl md:text-[28px] font-display font-black tracking-tight text-white">
                Publish your SOUL. Share the link. Get every new fan to try our AI.
              </div>
              <div className="mt-2 text-[13.5px] text-white/65 max-w-2xl">
                Every persona has its own shareable URL. Post it on Twitter/LinkedIn — anyone who opens it lands on the builder with your soul already loaded.
              </div>
            </div>
            <Button
              onClick={() => setSubmitOpen(true)}
              data-testid="souls-cta-publish-bottom"
              className="rounded-full text-black font-semibold px-6 py-6 text-[14px] hover:scale-[1.02] transition"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              Publish your SOUL <ArrowRight size={15} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <div className="h-24" />
      <SiteFooter />

      <SubmitSoulModal
        open={submitOpen}
        onClose={closeSubmit}
        onCreated={(s) => { closeSubmit(); loadSouls(); toast({ title: '🎉 Published!', description: `${s.name} is live at /souls/${s.slug}` }); }}
        remixSource={remixSource}
      />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster />
    </div>
  );
}

/* ---------- Soul Card ---------- */
function SoulCard({ soul, featured, onActivate, onShare, onLike }) {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
      className={`group relative rounded-2xl border ${featured ? 'border-white/20' : 'border-white/10'} bg-black/40 backdrop-blur-xl overflow-hidden`}
      data-testid={`soul-card-${soul.slug}`}
    >
      {/* Gradient header */}
      <div
        className="h-24 relative overflow-hidden"
        style={{ background: gradientFor(soul.gradient) }}
      >
        <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{
          background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 45%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.3), transparent 45%)',
        }} />
        <div className="absolute bottom-3 left-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-2xl">
            {soul.emoji || '✨'}
          </div>
          <div>
            <div className="text-[15px] font-black text-white leading-tight drop-shadow">
              {soul.name}
            </div>
            <div className="text-[10.5px] text-white/80 mt-0.5 flex items-center gap-1">
              <Users size={10} /> by {soul.author_name || 'Anonymous'}
            </div>
          </div>
        </div>
        {featured && (
          <div className="absolute top-3 right-3 rounded-full bg-black/50 backdrop-blur-md border border-white/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white flex items-center gap-1">
            <Star size={10} className="text-amber-300" fill="currentColor" /> Featured
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <p className="text-[13px] text-white/70 leading-relaxed line-clamp-2 min-h-[38px]">
          {soul.tagline}
        </p>

        <div className="mt-3 flex items-center gap-3 text-[11px] text-white/50">
          <button
            onClick={() => onLike(soul)}
            data-testid={`soul-like-${soul.slug}`}
            className="inline-flex items-center gap-1 hover:text-rose-300 transition"
          >
            <Heart size={12} /> {soul.likes}
          </button>
          <span className="inline-flex items-center gap-1">
            <Wand2 size={12} /> {soul.uses} uses
          </span>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => onActivate(soul)}
            data-testid={`soul-use-${soul.slug}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl text-black text-[12.5px] font-semibold py-2.5 hover:scale-[1.02] transition"
            style={{ background: gradientFor(soul.gradient) }}
          >
            Use this SOUL <ArrowRight size={13} />
          </button>
          <Link
            to={`/souls/${soul.slug}`}
            data-testid={`soul-open-${soul.slug}`}
            className="h-10 w-10 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white flex items-center justify-center transition"
            title="View details"
          >
            <ArrowRight size={14} />
          </Link>
          <button
            onClick={() => onShare(soul)}
            data-testid={`soul-share-${soul.slug}`}
            className="h-10 w-10 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white flex items-center justify-center transition"
            title="Copy share link"
          >
            <Share2 size={13} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- Submit Modal ---------- */
const GRADIENT_OPTIONS = Object.keys(GRADIENT_MAP);

function SubmitSoulModal({ open, onClose, onCreated, remixSource }) {
  const [form, setForm] = useState({
    name: '',
    tagline: '',
    system_prompt: '',
    author_name: '',
    author_link: '',
    author_email: '',
    author_handle: '',
    emoji: '',
    gradient: 'cyan-magenta',
  });
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill from remix source (opens with a light edit-first)
  useEffect(() => {
    if (open && remixSource) {
      setForm((f) => ({
        ...f,
        name: `${remixSource.name} — Remix`,
        tagline: remixSource.tagline || '',
        system_prompt: remixSource.system_prompt || '',
        emoji: remixSource.emoji || '',
        gradient: remixSource.gradient || 'cyan-magenta',
      }));
    }
  }, [open, remixSource]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.tagline.trim() || !form.system_prompt.trim()) {
      toast({ title: 'Missing fields', description: 'Name, tagline and prompt are required.' });
      return;
    }
    setSubmitting(true);
    try {
      // Get or generate the browser-scoped author key so this creator can see their souls on /souls/mine
      let authorKey = localStorage.getItem('atw_author_key');
      if (!authorKey) {
        authorKey = 'ak_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('atw_author_key', authorKey);
      }
      const r = await fetch(`${API}/souls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, author_key: authorKey, remixed_from: remixSource?.slug || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to publish');
      onCreated?.(d);
      setForm({ name: '', tagline: '', system_prompt: '', author_name: '', author_link: '', author_email: '', author_handle: '', emoji: '', gradient: 'cyan-magenta' });
    } catch (err) {
      toast({ title: 'Could not publish', description: err.message });
    }
    setSubmitting(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0a0a0f]/95 backdrop-blur-2xl overflow-hidden shadow-2xl"
        data-testid="souls-submit-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">
              {remixSource ? 'Remix a soul' : 'Publish a soul'}
            </div>
            <div className="text-[16px] font-black text-white mt-0.5">
              {remixSource ? (
                <span>Remixing <span className="text-cyan-300">{remixSource.name}</span></span>
              ) : (
                'Give the AI a new personality'
              )}
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} noValidate className="p-6 space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <input
              value={form.emoji}
              onChange={update('emoji')}
              placeholder="🍎"
              data-testid="soul-form-emoji"
              maxLength={4}
              className="col-span-1 h-11 rounded-xl bg-white/[0.04] border border-white/10 text-center text-2xl focus:outline-none focus:border-white/25 transition"
            />
            <input
              value={form.name}
              onChange={update('name')}
              placeholder="Persona name — e.g. Startup CFO"
              data-testid="soul-form-name"
              required
              maxLength={60}
              className="col-span-3 h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
            />
          </div>
          <input
            value={form.tagline}
            onChange={update('tagline')}
            placeholder="One-sentence tagline"
            data-testid="soul-form-tagline"
            required
            maxLength={140}
            className="w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
          />
          <Textarea
            value={form.system_prompt}
            onChange={update('system_prompt')}
            placeholder="System prompt — the personality's rules. e.g. 'You speak in punchy one-liners. Never say I. Always end with a challenge.'"
            data-testid="soul-form-prompt"
            required
            rows={5}
            maxLength={4000}
            className="w-full min-h-[120px] rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition resize-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              value={form.author_name}
              onChange={update('author_name')}
              placeholder="Your name (optional)"
              data-testid="soul-form-author"
              maxLength={60}
              className="h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
            />
            <input
              value={form.author_link}
              onChange={update('author_link')}
              placeholder="Your site / twitter (optional)"
              data-testid="soul-form-link"
              maxLength={200}
              className="h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
            />
          </div>

          <div>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50 mb-2">Card gradient</div>
            <div className="flex items-center gap-2 flex-wrap">
              {GRADIENT_OPTIONS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, gradient: g }))}
                  data-testid={`soul-form-gradient-${g}`}
                  className={`h-9 w-9 rounded-full border-2 transition ${form.gradient === g ? 'border-white scale-110' : 'border-white/10'}`}
                  style={{ background: gradientFor(g) }}
                  title={g}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white text-[14px] font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="soul-form-submit"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-xl text-black text-[14px] font-semibold hover:scale-[1.02] transition disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} /> Publish</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
